// Balises d'activité vers le backend, qui les relaie sur Telegram.
//
// Ne concerne QUE le haut de tunnel : arrivée sur le site, clic sur un
// bouton. L'inscription et le paiement sont notifiés côté serveur
// (register.php et le webhook Stripe) — là où ni un bloqueur de publicité ni
// un plaisantin ne peuvent rien fausser.
//
// `sendBeacon` et non `fetch` : la requête part même si la page se ferme ou
// navigue dans la seconde, ce qui est précisément le cas d'un clic sur un
// bouton. Elle ne bloque rien et ne rend aucune réponse.
// L'adresse du backend vient de api.js, source unique lue depuis .env : une
// seule ligne à changer quand ngrok redémarre.
import { BASE } from './api'
import { trackingAllowed, DEV_MODE } from './tracking'

// Une balise par PAGE VUE, dédoublonnée par (session + chemin) : recharger la
// même page n'écrit pas deux fois, mais passer d'une page à l'autre ajoute une
// ligne au journal. C'est ce qui rend la page Logs utile — on voit le parcours,
// et on voit qui revient.
//
// Ça ne noie pas Telegram pour autant : le serveur garde son délai de garde par
// IP (un message par heure) et journalise le reste sans envoyer
// (notif_log.throttled, migration 022).
const VISIT_KEY = 'ctrlurself.visit.v2'

function beacon (kind, label) {
  // Mode test (REACT_APP_DEV_MODE=true) : rien ne part. Indispensable ici et
  // pas seulement côté PHP — REACT_APP_API_URL pointe sur le backend de
  // PRODUCTION, qui notifierait Telegram pour de vrai.
  if (DEV_MODE) {
    // eslint-disable-next-line no-console
    console.info('[mode test] balise non envoyée —', kind, label ?? '')
    return
  }

  // Même porte que le pixel : refuser le suivi coupe aussi ces balises. Ce
  // n'est pas obligatoire pour de la mesure interne, mais promettre
  // « personne ne le saura » et pinguer quand même serait indéfendable.
  if (!trackingAllowed()) return
  if (typeof navigator === 'undefined') return

  // Le TYPE passe par l'URL, le LIBELLÉ par le corps. Les libellés sont des
  // textes de boutons accentués : si le corps arrive abîmé, le serveur perd
  // le libellé mais garde l'événement. Mélangés, tout disparaissait.
  const url = `${BASE}/track/event.php?k=${encodeURIComponent(kind)}`
  const body = JSON.stringify({ label })

  try {
    // text/plain : c'est le seul type que sendBeacon envoie sans déclencher
    // une requête de pré-vérification CORS, qu'un beacon ne survivrait pas.
    const blob = new Blob([body], { type: 'text/plain;charset=UTF-8' })
    if (navigator.sendBeacon?.(url, blob)) return
  } catch { /* on tente le repli */ }

  // Repli pour les navigateurs qui refusent sendBeacon.
  try {
    fetch(url, {
      method: 'POST', body, keepalive: true,
      headers: { 'Content-Type': 'text/plain' }
    }).catch(() => {})
  } catch { /* tant pis */ }
}

/** Arrivée sur le site. Une fois par session, avec la page d'entrée. */
export function pingVisit () {
  const path = window.location.pathname

  // L'espace client n'est pas une « visite » : c'est le client déjà entré, et
  // ses allées et venues rempliraient le journal pour rien.
  if (path.startsWith('/dashboard') || path.startsWith('/welcome')) return

  try {
    const seen = JSON.parse(sessionStorage.getItem(VISIT_KEY) || '[]')
    if (seen.includes(path)) return
    seen.push(path)
    sessionStorage.setItem(VISIT_KEY, JSON.stringify(seen.slice(-40)))
  } catch { /* navigation privée stricte : on notifie, sans dédoublonner */ }

  const params = new URLSearchParams(window.location.search)
  const ad = params.get('fbclid') ? ' · vient d’une publicité' : ''

  // ⚠️ JAMAIS la chaîne de requête telle quelle. Une adresse peut porter un
  // secret — la clé de la page d'administration, un jeton de réinitialisation,
  // un identifiant de session — et il se retrouverait écrit en clair dans le
  // journal ET dans un message Telegram. On ne garde que les paramètres de
  // provenance, ceux qui servent à savoir d'où vient le monde.
  const KEEP = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'ref', 'src', 'via']
  const kept = KEEP
    .filter(k => params.get(k))
    .map(k => `${k}=${params.get(k).slice(0, 40)}`)
    .join('&')

  beacon('visit', path + (kept ? '?' + kept : '') + ad)
}

/** Clic sur un bouton qui mène à l'inscription. */
export function pingCta (label) {
  beacon('cta', label)
}
